import { defineStore } from 'pinia'
import JwtService from '../services/jwt.service'
import AuthService from '../services/auth.service'
import { ApiService } from '../services/api.service'

interface LoginCredentials {
  username: string;
  password: string;
}

interface UserData {
  id: number;
  username: string;
  email: string;
  firstname: string;
  lastname: string;
  isAdmin: number;
  isSuperAdmin: number;
  isActive: number;
  d_created: string;
  d_updated: string | null;
  lastLogin: string;
  uuid: string;
  reset_required?: boolean;
}

export const useUserStore = defineStore('user', {
  state: () => {
    return {
      user: null as UserData | null,
      isAuthenticated: JwtService.hasToken(),
      isLoading: false,
      userName: '',
      email: '',
      memberSince: '',
      pfp: '',
      is2FAEnabled: false,
      isAdmin: false,
      fetchPromise: null as Promise<any> | null,
    }
  },

  actions: {
    toggle2FA() {
      this.is2FAEnabled = !this.is2FAEnabled
    },

    changeUserName(firstName: string, lastName: string) {
      this.userName = `${firstName} ${lastName}`.trim()
    },

    async fetchCurrentUser(forceRefresh = false) {
      // If there's already a fetch in progress, return that promise
      if (this.fetchPromise) {
        return this.fetchPromise;
      }

      // If we have user data and it's not a forced refresh, return existing data
      if (!forceRefresh && this.user) {
        return Promise.resolve(this.user);
      }

      this.isLoading = true;
      // Store the promise so other calls can use it
      this.fetchPromise = (async () => {
        try {
          const response = await AuthService.getCurrentUser();
          this.user = response.data;

          // Update the user profile data
          this.userName = `${response.data.firstname} ${response.data.lastname}`.trim();
          this.email = response.data.email;
          this.isAdmin = Boolean(response.data.isAdmin);

          // Format membership date
          if (response.data.d_created) {
            const date = new Date(response.data.d_created);
            this.memberSince = date.toLocaleDateString();
          }

          this.isAuthenticated = true;
          return response.data;
        } catch (error) {
          this.user = null;
          this.isAuthenticated = false;
          this.isAdmin = false;
          JwtService.destroyToken();
          throw error;
        } finally {
          this.isLoading = false;
          this.fetchPromise = null;
        }
      })();

      return this.fetchPromise;
    },

    async verifyAuth() {
      if (!JwtService.hasToken()) {
        this.isAuthenticated = false;
        this.user = null;
        this.isAdmin = false;
        return;
      }
      this.isLoading = true;
      try {
        // This assumes getCurrentUser also serves to verify the token
        const response = await AuthService.getCurrentUser();
        this.user = response.data;
        this.userName = `${response.data.firstname} ${response.data.lastname}`.trim();
        this.email = response.data.email;
        this.isAdmin = Boolean(response.data.isAdmin);
        if (response.data.d_created) {
          const date = new Date(response.data.d_created);
          this.memberSince = date.toLocaleDateString();
        }
        this.isAuthenticated = true;
      } catch (error) {
        this.purgeAuth(); // Use purgeAuth to clean up
        throw error; // Re-throw so the router can catch it
      } finally {
        this.isLoading = false;
      }
    },

    purgeAuth() {
      JwtService.destroyToken();
      this.user = null;
      this.userName = '';
      this.email = '';
      this.memberSince = '';
      this.pfp = '';
      this.isAdmin = false;
      this.isAuthenticated = false;
      ApiService.setHeader(); // Clear auth header
    },

    // Call this when you need to refresh user data
    async refreshUserData() {
      return this.fetchCurrentUser(true);
    },

    async login(credentials: LoginCredentials) {
      this.isLoading = true;
      try {
        const response = await AuthService.login(credentials);
        if (response.status === 'success' && response.data) {
          // Save token
          JwtService.saveToken(response.data.access_token);

          // Save user data
          const userData = response.data.user;
          this.user = userData;

          // Update user profile data
          this.userName = `${userData.firstname} ${userData.lastname}`.trim();
          this.email = userData.email;
          this.isAdmin = Boolean(userData.isAdmin);

          // Format membership date
          if (userData.d_created) {
            const date = new Date(userData.d_created);
            this.memberSince = date.toLocaleDateString();
          }

          this.isAuthenticated = true;
          ApiService.setHeader();
          return true;
        }

        throw new Error('Invalid login response format');
      } catch (error) {
        this.purgeAuth();
        throw error;
      } finally {
        this.isLoading = false;
      }
    },

    async logout() {
      console.log('logout')
      this.purgeAuth(); // Use purgeAuth to perform the logout actions
    }
  }
})